'use client';

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import usePlacesAutocomplete, {
    getGeocode,
    getLatLng,
} from 'use-places-autocomplete';
import SearchAutocompleteList from './SearchAutocompleteList';

SearchField.propTypes = {
    onSelectAddress: PropTypes.func.isRequired,
    onRadiusChange: PropTypes.func.isRequired,
    defaultAddressValue: PropTypes.object,
    defaultRadiusValue: PropTypes.number,
};

const INPUT_DEBOUNCE = 300;
const AUTOCOMPLETE_CACHE_TIMER = 60 * 60 * 24;
const PLACES_TYPE_RESTRICTIONS = ['(regions)'];
const PLACES_COUNTRY_RESTRICTION = 'uk';

export default function SearchField({
    onSelectAddress,
    onRadiusChange,
    defaultAddressValue,
    defaultRadiusValue,
}) {
    const [usingCurrentLocation, setUsingCurrentLocation] = useState(false);
    const placesLibrary = useMapsLibrary('places');

    const {
        init,
        ready,
        value,
        setValue,
        suggestions: { status, data },
        clearSuggestions,
    } = usePlacesAutocomplete({
        initOnMount: false,
        debounce: INPUT_DEBOUNCE,
        defaultValue: defaultAddressValue.address ? defaultAddressValue.address : '',
        cache: AUTOCOMPLETE_CACHE_TIMER,
        requestOptions: {
            types: PLACES_TYPE_RESTRICTIONS,
            componentRestrictions: { country: PLACES_COUNTRY_RESTRICTION },
        },
    });

    useEffect(() => {
        if (!placesLibrary) return;

        init();
    }, [placesLibrary]);

    const handleChange = (e) => {
        setValue(e.target.value);
        setUsingCurrentLocation(false);

        if (e.target.value === '') {
            onSelectAddress(null);
        }
    };

    const handleSelect = async (selected) => {
        const address = selected.description;
        setValue(address, false);
        searchForAddress(address);
    };

    const searchForAddress = async (address) => {
        clearSuggestions();

        try {
            const results = await getGeocode({ address });
            const { lat, lng } = getLatLng(results[0]);

            document.dispatchEvent(new CustomEvent('select-location', {detail: { address, lat, lng }}));
            onSelectAddress({ address, lat, lng });
        } catch (error) {
            console.error('Error: ', error);
        }
    };

    const handleRadius = (e) => {
        const radius = parseInt(e.target.value, 10);
        onRadiusChange(radius);
        document.dispatchEvent(new CustomEvent('select-radius', {detail: radius}));
    };

    const handleCurrentLocation = () => {
        if (usingCurrentLocation) {
            setValue('');
            onSelectAddress(null);
            setUsingCurrentLocation(false);
            return;
        }
        setUsingCurrentLocation(true);

        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }

        navigator.geolocation.getCurrentPosition(
            async ({ coords }) => {
                const { latitude, longitude } = coords;
                const results = await getGeocode({
                    location: { lat: latitude, lng: longitude },
                });
                const { formatted_address } = results[0];

                onSelectAddress({
                    address: formatted_address,
                    lat: latitude,
                    lng: longitude,
                });
                setValue(formatted_address);
            },
            () => {
                alert('Unable to retrieve your location');
            }
        );
    };

    return (
        <div className="searchField">
            <div className="searchField__col">
                <label>Search Area *</label>
                <div className="u-relative">
                    <div className="searchField__icon">
                        <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M11 19.5C15.4183 19.5 19 15.9183 19 11.5C19 7.08172 15.4183 3.5 11 3.5C6.58172 3.5 3 7.08172 3 11.5C3 15.9183 6.58172 19.5 11 19.5Z" stroke="#213163" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M21 21.4999L16.65 17.1499" stroke="#213163" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <div
                        className={`searchField__uselocation ${
                            usingCurrentLocation
                                ? 'searchField__uselocation--disabled'
                                : ''
                        }`}
                        onClick={handleCurrentLocation}
                    >
                        <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22.5C17.5228 22.5 22 18.0228 22 12.5C22 6.97715 17.5228 2.5 12 2.5C6.47715 2.5 2 6.97715 2 12.5C2 18.0228 6.47715 22.5 12 22.5Z" stroke="#E2231A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M22 12.5H18" stroke="#E2231A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M6 12.5H2" stroke="#E2231A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 6.5V2.5" stroke="#E2231A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 22.5V18.5" stroke="#E2231A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </div>
                    <input
                        className="searchField__input"
                        type="text"
                        placeholder="Search for a place"
                        value={value}
                        onChange={handleChange}
                        disabled={!ready}
                        autoComplete="off"
                    />
                    {status === 'OK' && !usingCurrentLocation && value && (
                        <SearchAutocompleteList
                            onSelect={handleSelect}
                            results={data}
                        />
                    )}
                </div>
            </div>
            <div className="searchField__col">
                <label>Within</label>
                <select
                    className="searchField__select"
                    onChange={handleRadius}
                    defaultValue={defaultRadiusValue}
                >
                    <option value="5">5 miles</option>
                    <option value="10">10 miles</option>
                    <option value="15">15 miles</option>
                    <option value="20">20 miles</option>
                    <option value="50">50 miles</option>
                </select>
            </div>
        </div>
    );
}
